import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable } from 'mobx';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { CurrentWorkspaceChangedEventType } from '../../../modules/workspaces/currentWorkspace/events/CurrentWorkspaceChangedEvent';
import { ACDLError } from '../../models/error.model';
import { IMainApiService, IPagedResult } from '../../services/api.service';
import { IApiService } from '../../services/api.service.base';
import { EventListeningDisposer, IEventBus } from '../../services/eventBus.service';
import { IDatasetListItemViewModel, IDatasetsStore } from './datasets.store';

export interface IDatasetsService {
  init(initialPageSize?: number): void;
  dispose(): void;
  loadUserDatasetAsync(): Promise<void>;
  deleteDatasetAsync(datasetId: string): Promise<void>;
  changeOrder(orderBy: string | undefined, orderType: string | undefined): Promise<void>;
  paginationChange(pageNumber: number, pageSize: number): Promise<void>;
  markStale(): void;
  store: IDatasetsStore;
}

export namespace IDatasetsService {
  export const $: interfaces.ServiceIdentifier<IDatasetsService> = Symbol('DATASETS_SERVICE');
}

@injectable()
export class DatasetsService implements IDatasetsService {
  disposers: EventListeningDisposer[] = [];
  constructor(
    @inject(IMainApiService.$) private readonly apiService: IApiService,
    @inject(IDatasetsStore.$) readonly store: IDatasetsStore,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore,
    @inject(IEventBus.$) private readonly eventBus: IEventBus
  ) {
    makeObservable(this);
  }

  @action
  init = (initialPageSize?: number): void => {
    if (initialPageSize) {
      this.store.datasetsPaging.pageSize = initialPageSize;
    }

    this.disposers = [this.eventBus.addListener(this.currentWorkspaceChangedListener, CurrentWorkspaceChangedEventType)];
  };

  @action
  dispose = (): void => {
    this.disposers.forEach((d) => {
      d();
    });
    this.disposers = [];
  };

  @action
  markStale = (): void => {
    this.store.stale = true;
  };

  @action
  changeOrder = async (orderBy: string | undefined, orderType: string | undefined): Promise<void> => {
    this.store.datasetsPaging.orderBy = orderBy || '';
    this.store.datasetsPaging.orderType = orderType || '';
    await this.loadUserDatasetAsync();
  };

  @action
  paginationChange = async (pageNumber: number, pageSize: number): Promise<void> => {
    this.markStale();
    this.store.datasetsPaging.pageNumber = pageNumber;
    this.store.datasetsPaging.pageSize = pageSize;
    await this.loadUserDatasetAsync();
  };

  @action
  loadUserDatasetAsync = async (): Promise<void> => {
    const workspaceId = this.currentWorkspaceStore.currentWorkspace.id;
    if (!workspaceId) {
      return;
    }

    this.markStale();

    const { pageNumber, pageSize, orderBy, orderType } = this.store.datasetsPaging;

    const params = { workspaceId, pageNumber, pageSize, orderBy, orderType };

    const result = await this.apiService.getAsync<IPagedResult<IDatasetListItemViewModel>>('/datasets', { params });

    if (result instanceof ACDLError) {
      return;
    }

    if (result.pagesCount < result.pageNumber) {
      this.store.datasetsPaging.pageNumber = result.pagesCount;
      await this.loadUserDatasetAsync();
      return;
    }

    const paging = { ...this.store.datasetsPaging, pageNumber: result.pageNumber, pagesCount: result.pagesCount, totalCount: result.totalCount };
    this.store.setDatasets(result.data, workspaceId, paging);
  };

  @action
  deleteDatasetAsync = async (datasetId: string): Promise<void> => {
    this.store.stale = true;
    const url = `/datasets/remove?DatasetId=${datasetId}`;
    const result = await this.apiService.postAsync(url);
    if (result instanceof ACDLError) {
      throw result;
    }
    await this.loadUserDatasetAsync();
  };

  private currentWorkspaceChangedListener = async (): Promise<void> => this.loadUserDatasetAsync();
}
